<template>
  <div class="fields-module">
    <div
      v-if="showNextTemplateButton || selectedInstanceIndex"
      class="d-flex justify-content-between mb-3"
    >
      <b-button-group size="sm">
        <b-button
          v-if="showNextTemplateButton"
          variant="outline-primary"
          @click="moveInstanceSelection(1)"
        >
          <b-icon icon="arrow-down" />
          Next template
        </b-button>
        <b-button
          v-if="selectedInstanceIndex"
          variant="outline-primary"
          @click="moveInstanceSelection(-1)"
        >
          Prev template
          <b-icon icon="arrow-up" />
        </b-button>
      </b-button-group>
      <span>{{ totalTemplateOrder }}/{{ totalTemplatesCount }}</span>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'textEditorFieldsSection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-fonts green-fonts" />
          <strong> Text Content</strong>
        </div>
        <b-icon
          :icon="
            isOpen.textEditorFieldsSection ? 'chevron-up' : 'chevron-down'
          "
        />
      </div>
      <transition>
        <b-collapse 
          :visible="isOpen.textEditorFieldsSection"
        >
          <div>
            <b-form-group
              v-for="(field, index) in textFields"
              :key="`${field.element_id}_${index}`"
              :label-for="`field_${field.id}`"
            >
              <FormGroupCharLimit
                :label="field.name"
                :value="selectedInstance.data[field.element_id]"
                :max="getMaxCharsValue(field)"
              >
                <component
                  :is="field.type === 'Multi Line Text' && String(selectedInstance?.data?.[field.element_id] ?? '').includes('\n') ? 'b-textarea' : 'b-input'"
                  :id="`field_${field.element_id}`"
                  :value="selectedInstance.data[field.element_id]"
                  @focus="setActiveObject(field)"
                  @input="updateField(field, $event)"
                  @click.stop
                  @blur="onBlurText"
                />
              </FormGroupCharLimit>
              <div
                v-if="shouldShowThisField(field)"
                class="d-flex align-items-baseline"
              >
                <b-button
                  class="mt-1 btn-sm w-50 maintain-button-width"
                  variant="primary"
                  @click="redraw(field)"
                >
                  Align to <strong>{{ JSON.parse(field.options).Option1 }}</strong>
                </b-button>

                <b-checkbox
                  v-show="false"
                  :checked="getAutoPositionValue(field)"
                  class="ml-1 w-40"
                  @change="onChangeAutoPositionValue(field, $event)"
                >
                  Auto position
                </b-checkbox>
              </div>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'translateFieldsSelection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-translate green-fonts" />
          <strong> Translate</strong>
        </div>
        <b-icon
          :icon="
            isOpen.translateFieldsSelection ? 'chevron-up' : 'chevron-down'
          "
        />
      </div>
      <transition>
        <b-collapse
          :visible="isOpen.translateFieldsSelection"
        >
          <div>
            <b-form-group v-if="textFields.length === 0">
              This template has no text fields
            </b-form-group>
            <b-form-group
              v-if="textFields.length > 0"
              class="translation-form-group"
            >
              <div class="d-flex flex-column">
                <Multiselect
                  v-model="translateTargetLanguages"
                  class="flex-grow-1 form-control px-1 py-0 font-base flex items-center ms-bootstrap-size"
                  style="width: auto !important; height: auto !important;"
                  :options="languages"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  placeholder="Choose language(s)..."
                />
                <b-button
                  v-if="translateTargetLanguages.length <= 1"
                  class="flex-shrink-0 w-100 mt-2"
                  @click="translate"
                >
                  Translate
                </b-button>
                <b-button
                  class="flex-shrink-0 w-100 mt-2"
                  @click="copyAndTranslate"
                >
                  <span>Copy Template & Translate</span>
                  <b-badge
                    v-if="translateTargetLanguages.length > 1"
                    variant="dark"
                    class="ml-2"
                  >
                    {{ translateTargetLanguages.length }}
                  </b-badge>
                </b-button>
                <b-button
                  v-if="translateTargetLanguages.length <= 1"
                  class="flex-shrink-0 w-100 mt-2"
                  @click="copyFieldsAndTranslate"
                >
                  Copy Fields & Translate
                </b-button>
              </div>
            </b-form-group>
            <TranslateImageButton :target-languages="translateTargetLanguages" />
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'backgroundFieldsSection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-palette green-fonts" />
          <strong> Background</strong>
        </div>
        <b-icon
          :icon="
            isOpen.backgroundFieldsSection ? 'chevron-up' : 'chevron-down'
          "
        />
      </div>
      <transition>
        <b-collapse :visible="isOpen.backgroundFieldsSection">
          <div>
            <b-form-group>
              <div class="d-flex justify-content-between align-items-center flex-column">
                <div class="colors-item d-flex justify-content-between align-items-center">
                  <label class="mb-1">
                    <strong style="color: #209c86">Color</strong>
                  </label>
                  <div
                    v-for="(color, index) in editor?.colors"
                    :key="index"
                    class="d-flex align-items-center"
                  >
                    <b-input
                      v-model="editor.colors[index]"
                      class="color-fill-item w-10 no-right-border custom-color-picker"
                      debounce="50"
                      type="color"
                      @update="redrawBackground"
                    />
                    <b-input
                      v-model="editor.colors[index]"
                      style="height: 38px;"
                      class="w-100 no-left-border"
                      type="text"
                      @change="onInputColor($event, index)"
                    />
                  </div>
                </div>
                <div
                  class="d-flex flex-row align-items-center mb-1 position-relative w-100"
                  style="margin-top: 10px;"
                >
                  <label
                    class="mb-0 mr-2"
                    style="min-width: 91.5px;"
                  >
                    <strong style="color: #209c86">Image</strong>
                  </label>

                  <div
                    class="d-flex flex-row justify-content-between flex-grow-1 image-multiselect"
                    :class="{ 'drag-over': isDragOver }"
                    @dragenter.prevent="onDragEnter"
                    @dragover.prevent="onDragOver"
                    @drop.prevent="onDrop"
                  >
                    <Multiselect
                      v-model="selectedBackground"
                      class="background-dropdown flex-grow-1"
                      track-by="id"
                      label="name"
                      placeholder=""
                      :options="getBackgroundFields"
                      @close="onBlurText"
                      @open="getFields"
                    >
                      <template #singleLabel="props">
                        <div
                          class="background-dropdown-label"
                          @mouseenter="hoveredTag = true"
                          @mouseleave="hoveredTag = null"
                        >
                          <div class="background-dropdown-label__image">
                            <img
                              :src="props.option.thumbnail"
                              :alt="props.option.name"
                              class="preview-img"
                              @click.stop.prevent
                            >
                          </div>
                        </div>
                      </template>
                      <template #option="props">
                        <div class="background-dropdown-option">
                          <div class="background-dropdown-option__image">
                            <img
                              :src="props.option.thumbnail"
                              :alt="props.option.name"
                            >
                          </div>
                          <p class="background-dropdown-option__text">
                            {{ props.option.name }}
                          </p>
                        </div>
                      </template>
                      <template #caret>
                        <span class="custom-arrow">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#333"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </template>
                    </Multiselect>
                    <b-button
                      variant="outline-primary"
                      class="upload-button d-flex align-items-center justify-content-center"
                      @click.stop.prevent="onChange('selectBackground', 'Replace')"
                    >
                      <b-icon icon="upload" />
                      <span style="margin-left: 10px;" />
                    </b-button>
                  </div>
                </div>
              </div>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div
      class="layout-sidebar-section"
    >
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'productImagesFieldsSection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-file-earmark-image green-fonts" />
          <strong> Product Images</strong>
        </div>
        <b-icon
          :icon="
            isOpen.productImagesFieldsSection ? 'chevron-up' : 'chevron-down'
          "
        />
      </div>
      <transition>
        <b-collapse :visible="isOpen.productImagesFieldsSection">
          <div class="scroll-images-section">
            <div 
              class="d-flex flex-wrap align-items-center"
              :class="{ 'drag-over': isDragOverProductImages }"
              @dragenter.prevent="onDragEnterProductImages"
              @dragover.prevent="onDragOverProductImages"
              @dragleave.prevent="onDragLeaveProductImages"
              @drop.prevent="onDropProductImage"
            >
              <div
                v-for="(image, index) in getProductImages"
                :key="`product_image_${image.templateFieldId ?? image.id ?? 'na'}_${index}`"
                class="label-option general-card-template product-card-template"
                :class="{ selected: layoutTarget?.id === image.id }"
                @drop.stop.prevent="(e) => onDropReplaceProductImage(e, image)"
                @dragover.prevent
              >
                <button 
                  class="close-btn"
                  @click.stop="handleProductDelete(image)" 
                >
                  <i class="bi bi-trash3" />
                </button>
                <button
                  v-b-tooltip.hover.viewport.noninteractive="'Replace image'"
                  class="replace-image-btn"
                  @click="toggleProductDropdown(image, 'replace')"
                >
                  <i class="bi bi-repeat" />
                </button>
                <img 
                  class="image-option"
                  :src="image.thumbnail" 
                  :alt="image.name"
                  @click.stop="setActiveObject(image)"
                >
              </div>
              <div 
                style="width: 60px;height: 60px;"
                class="d-flex align-items-center justify-content-center additional-card-template"
                @click="toggleProductDropdown(null, 'add')"
              >
                <i 
                  v-b-tooltip.hover.top
                  title="Add product images"
                  class="fas fa-plus-circle add-icon" 
                />
              </div>
            </div>
            <b-button
              v-if="!isDisplayedProductImagesSection"
              v-b-tooltip.hover.top
              title="Replace product images"
              variant="outline-primary"
              class="upload-button d-flex align-items-center justify-content-center"
              @click="toggleProductDropdown(null, 'replace_all')"
            >
              <i class="bi bi-repeat add-icon" /> <span style="margin-left: 10px; margin-right: 10px;"><strong>Replace</strong></span>
            </b-button>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'imagesFieldSection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-card-image green-fonts" />
          <strong>Images</strong>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-if="!textImagesClassified && getImages.length > 0"
            size="sm"
            variant="outline-primary"
            class="mr-2 detect-text-btn"
            :disabled="isClassifyingTextImages"
            @click.stop="handleDetectTextImages"
          >
            <b-spinner
              v-if="isClassifyingTextImages"
              small
            />
            <i
              v-else
              class="bi bi-fonts"
            />
            <span class="ml-1">Detect Text</span>
          </b-button>
          <b-icon
            :icon="
              isOpen.imagesFieldSection ? 'chevron-up' : 'chevron-down'
            "
          />
        </div>
      </div>
      <transition>
        <b-collapse :visible="isOpen.imagesFieldSection">
          <div
            class="scroll-images-section"
            @drop.prevent="onDropAddImage"
            @dragover.prevent
          >
            <div 
              class="d-flex flex-wrap align-items-center"
            >
              <div
                v-for="(image) in getImagesWithUuid"
                :key="image._uuid"
                class="label-option general-card-template image-card-template"
                :class="{ selected: layoutTarget?.id === image.id }"
                @drop.stop.prevent="(e) => onDropReplaceLayoutImage(e, image)"
                @dragover.prevent
              >
                <div 
                  v-if="!loaded" 
                  class="image-loader"
                >
                  <b-spinner small />
                </div>
                <img 
                  class="image-option"
                  :class="{ 'is-loading': !loaded }"
                  :src="image.thumbnail" 
                  :alt="image.name"
                  @load="onImageLoad"
                  @error="onImageLoad"
                  @click.stop="setActiveObject(image)"
                >
                <button 
                  class="close-btn"
                  @click.stop.prevent="handleImageDelete(image)" 
                >
                  <i class="bi bi-trash3" />
                </button>
                <button
                  v-b-tooltip.hover.viewport.noninteractive="'Replace image'"
                  class="replace-image-btn"
                  @click.stop.prevent="replaceImage(image, 'selectBackground', 'Replace', null, selectImageOpenSources.FIELDS_REPLACE_IMAGE)"
                >
                  <i class="bi bi-repeat" />
                </button>
                <button
                  v-if="isTextOnlyImage(image)"
                  v-b-tooltip.hover.viewport.noninteractive="'Convert to editable text'"
                  class="convert-text-btn"
                  :disabled="convertingImageId === image.id"
                  @click.stop.prevent="handleConvertToText(image)"
                >
                  <b-spinner
                    v-if="convertingImageId === image.id"
                    small
                  />
                  <i
                    v-else
                    class="bi bi-fonts"
                  />
                </button>
              </div>
              <div 
                class="d-flex align-items-center justify-content-center additional-card-template"
                @click="addAdditionalField('Background Theme Image', null, selectImageOpenSources.FIELDS_ADD_IMAGES)"
              >
                <i 
                  v-b-tooltip.hover.top
                  title="Add images"
                  class="fas fa-plus-circle add-icon"
                />
                <i 
                  v-if="getImages.length > 0"
                  v-b-tooltip.hover.top
                  title="Replace images"
                  class="bi bi-repeat add-icon" 
                  style="margin-left: 7px;"
                  @click.stop="replaceAllImage('selectBackground', 'Replace')" 
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="position-fixed apply-to-section">
      <b-form-group>
        <span>Apply to:</span>
        <SelectTemplateDropdown
          class="d-inline"
          :local-storage-key="LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT"
          :clear-local-storage="false"
          :show-apply-button="true"
          @apply="confirmApply"
        />
      </b-form-group>
    </div>
    <ProductImageDropdown
      ref="productImageDropdownFields"
      v-model="fileNameIds"
      :is-tab-active="false"
      :is-file-image="options.isFileImage || options.isProductImageObjects"
      :is-fields-tab="true"
      @input="onChange('productImage', fileNameIds)"
      @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
    />

    <hr>
    <SelectAllObjectsButton @unselected="$emit('unselected')" />
    <b-button
      v-b-tooltip.hover.viewport.noninteractive="pasted ? 'Pasted!' : 'Paste element'"
      data-action="paste"
      variant="primary"
      @click="pasteElement"
    >
      <i :class="['cursor-pointer font-xl px-2 bi', pasted ? 'bi-check2' : 'bi-clipboard']" />
    </b-button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import { setSelectedInstance } from '@frontend/group/modules/copy-paste-object'
import { HANDLER_TYPES } from "@frontend/group/modules/translate-template";
import SelectTemplateDropdown
  from "@frontend/components/modules/layouts-page/EditorControl/components/SelectTemplateDropdown.vue";
import {GLOBAL_UPDATE_LOCAL_STORAGE_KEY, isGlobalUpdateOn} from "@frontend/constants/global-update";
import FormGroupCharLimit from "@frontend/components/common/atoms/FormGroupCharLimit.vue";
import { LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
  LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
  LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE
 } from "@frontend/group/modules/editor-control-popup/selected-template-for-apply-to-all";
 import {deepCopy} from '@frontend/utils';
import {FIELD_TYPE_BACKGROND_THEME_IMAGE} from '@frontend/constants/type-fields-of-template';
 import Multiselect from 'vue-multiselect';
 import { FIELD_NAME_IMAGE_AREA } from '@frontend/constants/name-fields-of-template';
 import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import SelectAllObjectsButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/SelectAllObjectsButton.vue'
import TranslateImageButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/TranslateImageButton.vue'
import { DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields'
import { getObjectsForOrder } from '@frontend/group/modules/copy-and-resize';
import { 
  IMAGE_PREFIX_ID, 
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID, 
  TEXT_PREFIX_ID, 
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  UPLOAD_IMAGE_PREFIX_ID,
  IMAGE_FROM_BK_PREFIX_ID,
  KEY_GROUP
 } from '@frontend/constants/templates-field-prefixes';
import ProductImageDropdown from './components/ProductImageDropdown.vue';
import {
  validateProductImage,
  uploadProductImageAndDraw,
  replaceProductImage
} from '@/frontend/group/modules/add-product-image/add-product-image-service';
import TemplateDragDropService from "@frontend/group/modules/template-events/template-drag-and-drop-service";
import ButtonPrimary from "@frontend/components/common/atoms/ButtonPrimary.vue";
import {COPY_PASTE_EVENTS, copyPasteEventBroker} from "@frontend/group/modules/copy-paste-object/event-broker";
import { v4 as uuid } from "uuid";
import { SELECT_IMAGE_OPEN_SOURCES } from '@frontend/constants/select-image-open-sources';
const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'layoutChanges',
  'getChangesLayoutById',
  'saveLayout',
  'updateLayoutChanges',
  'handleObjectMoving',
  'onClickDuplicateTemplateButton',
]);

const BG_THEME_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
];

const IMG_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  UPLOAD_IMAGE_PREFIX_ID,
  IMAGE_FROM_BK_PREFIX_ID,
];

export default {
  name: 'FieldsModule',
  components: {
    ButtonPrimary,
    SelectAllObjectsButton,
    FormGroupCharLimit,
    SelectTemplateDropdown,
    Multiselect,
    ProductImageDropdown,
    TranslateImageButton
  },
  props: {
    isOpen: {
      type: Object,
      default: () => ({
        textEditorFieldsSection: true,
        translateFieldsSelection: true,
        backgroundFieldsSection: true,
        productImagesFieldsSection: true,
        imagesFieldSection: true
      }),
    },
    options: {
      type: Object,
      default: () => {},
    },
    target: {
      type: Object,
      default: () => null,
      required: false,
    },
    defaultColor: {
      type: String,
      default: "#ffffff",
      required: false
    },
    typeEditorControl: {
      type: String,
      default: null,
      required: false
    }
  },
  data: () => ({
    selectImageOpenSources: SELECT_IMAGE_OPEN_SOURCES,
    translateTargetLanguages: [],
    languages: [
      'Arabic',
      'Bengali',
      'Cantonese',
      'Czech',
      'Dutch',
      'English',
      'French-CA',
      'French-FR',
      'German',
      'Greek',
      'Hebrew',
      'Hindi',
      'Italian',
      'Japanese',
      'Korean',
      'Polish',
      'Portuguese',
      'Russian',
      'Simplified Chinese',
      'Slovak',
      'Spanish (LatAm)',
      'Spanish (Spain/EU)',
      'Swedish',
      'Thai',
      'Traditional Chinese',
      'Turkish',
      'Vietnamese'
    ],
    loadingCopyFieldsAndTranslate: false,
    loadingCopyAndTranslate: false,
    loadingTranslate: false,
    isApplyToAllGlobalUpdate: false,
    editor: {},
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
    backgroundProperties: {
      selectedField: null,
    },
    fields: [],
    hoveredTag: null,
    isShowDropdown: false,
    isContracted: false,
    fileNameIds: '',
    loaded: false,
    imagesLoadedCount: 0,
    isDragOver: false,
    isDragOverProductImages: false,
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10mb
    pasted: false,
    // Convert Image to Text feature
    textImagesClassified: false,
    isClassifyingTextImages: false,
    textOnlyImageIds: new Set(),
    convertingImageId: null,
  }),

  computed: {
    ...mapGetters('layout', {
      layers: 'layers',
      allProductImages: 'allProductImages',
      selectedInstance: 'selectedInstance',
      selectedInstanceIndex: 'selectedInstanceIndex',
      selectedInstanceFieldsWithType: 'selectedInstanceFieldsWithType',
      selectedInstanceAdditionalFields: 'selectedInstanceAdditionalFields',
      selectedInstanceTextBoxFields: 'selectedInstanceTextBoxFields',
      currentPagePreviews: 'currentPagePreviews',
      layoutTarget: 'target', 
    }),
    textFields () {
      return this.selectedInstanceTextBoxFields.filter(field => field.visible === true);
    },
    multilineTextFields () {
      return this.selectedInstanceFieldsWithType('Multi Line Text')
    },
    additionalTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Text');
    },
    additionalMultilineTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Multi Line Text');
    },
    showNextTemplateButton () {
      return !!this.currentPagePreviews.at(this.selectedInstanceIndex + 1)
    },
    currentPage() {
      return +$('input[name="current_page"]').val()
    },
    perPage() {
      return +$('input[name="per_page"]').val()
    },
    totalTemplateOrder () {
      return this.selectedInstanceIndex + 1 + (this.currentPage - 1) * this.perPage
    },
    totalTemplatesCount () {
      return window.preview_data.searchTemplateDropdownItems.length
    },
    getBackgroundFields() {
      return this.fields.filter(field => BG_THEME_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible);
    },
    getProductImages() {
      this.getFields();

      const objectsFiltered = [...this.selectedInstance.canvas.getObjects()].reverse();
      //order product images based on layers
      const filteredFields = this.fields.filter(field => field?.id?.startsWith(IMAGE_PREFIX_ID) && field.visible);

      if (objectsFiltered?.length > 0) {
        return _.sortBy(filteredFields, field => objectsFiltered.findIndex(object => object.fieldId === field.templateFieldId));
      }

      return filteredFields;
    },
    isDisplayedProductImagesSection() {
      return this.getProductImages.length === 0 && this.allProductImages?.length;
    },
    getImages() {
      return this.getImagesFiltered();
    },
    getImagesWithUuid() {
      return this.getImages.map(img => {
        if (!img._uuid) img._uuid = uuid();
        return img;
      });
    },
    selectedBackground: {
      get() {
        const canvas = this.selectedInstance?.canvas;
        const objects = canvas.getObjects() || [];
        //preselect background image by searching for layer name or
        // by checking if canvas size is smaller than background image size
        return this.fields.find(option => option?.id === this.backgroundProperties.selectedField) || 
        this.getBackgroundFields.find(option => option?.name.toLowerCase().includes('background')) ||
        this.getBackgroundFields.find(option => objects.some(f => f.id === option.id && canvas.width <= f.width * f.scaleX && canvas.height <= f.height * f.scaleY)) ||
        null;
      },
      set(option) {
        this.backgroundProperties.selectedField = option ? option?.id : null;
        // this.setActiveObject(option);
      }
    },

  },
  watch: {
    getBackgroundFields(newOptions) {
      newOptions.map(item => {
        if(item.name === "Background"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Background Image"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Image Area"){
          this.backgroundProperties.selectedField = item.id;
        }
      });
    },
    target() {
      this.initEditor();
    },
    layoutTarget() {
      this.handleSectionsState();
      this.$nextTick(() => {
        const el = this.$el.querySelector('.selected')
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
  },
  mounted() {
    this.isApplyToAllGlobalUpdate = isGlobalUpdateOn();
    if (localStorage.getItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY) === null) {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);
    }
    editorControlEventBroker.on(EVENTS.TOGGLE_LOADING, this.toggleLoading.bind(this));
    editorControlEventBroker.on(EVENTS.GLOBAL_UPDATE, () => {
      this.isApplyToAllGlobalUpdate = !this.isApplyToAllGlobalUpdate;
      this.onChangeApplyToAllGlobal();
    });
    this.initEditor();
    this.getFields();
    this.initTextImageClassifications();
  },

  methods: {
    ...mapActions('fileBrowserProductSelector', [
      'disableMoveProductsMode',
      'enableReplaceMode',
      'setSelectedCanvasElement',
      'togglePickedItem',
      'clearSelection',
    ]),
    initEditor() {
      this.editor = this.target?.editor;
      if (this.editor?.colors?.length === 0) {
        this.editor.colors = [this.defaultColor];
      }
    },
    getFields() {
      const { previews } = getShowPageScope();
      const preview = previews.find(preview => preview.id === this.selectedInstance.id);
      if (!preview) {
        return;
      }
      const rawObjects = getObjectsForOrder(preview.canvas.getObjects());
      const objects = this.flattenFabricObjects(rawObjects);

      const fields = objects.map(object => {
        if (!object || !object.target) return null;

        let thumbnail = null;        

        if (object.target._element && object.id.startsWith(IMAGE_PREFIX_ID) && object?.target?._element?.currentSrc) {
          const url = new URL(object.target._element.currentSrc);
          if (url.searchParams.size > 0) {
            url.searchParams.set('lightweight', '');
            url.searchParams.set('scale_width', '40');
          }
          thumbnail = url;
        } else if (object.target._element && !object.id.startsWith(IMAGE_PREFIX_ID)) {
          const url = new URL(object.target._element.currentSrc);
          if (url.searchParams.size > 0) {
            url.searchParams.set('lightweight', '');
            url.searchParams.set('is_thumbnail', '1')
          }
          thumbnail = url;
        }
        
        return {
          id: object.target.id || '',
          name: object.name || '',
          type: object.target.templateField?.type || FIELD_TYPE_BACKGROND_THEME_IMAGE,
          thumbnail: thumbnail || DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images,
          visible: object.visible ?? null,
          templateFieldId: object.templateFieldId,
          fileName: object.fileName
        }
      })

      this.fields = fields;
      this.backgroundProperties.selectedField = this.getBackgroundFields
        .find(field => field.name === FIELD_NAME_IMAGE_AREA)?.id || null;
    },
    flattenFabricObjects(objects = []) {
      const result = [];

      objects.forEach(obj => {
        // Group object
        if (obj?.id?.startsWith(KEY_GROUP)) {
          const children = obj.getObjects
            ? obj.getObjects()
            : (obj.objects || obj._objects || []);

          result.push(...this.flattenFabricObjects(children));
        } else {
          result.push(obj);
        }
      });

      return result;
    },
    onChangeApplyToAllGlobal() {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, this.isApplyToAllGlobalUpdate);
      if (this.isApplyToAllGlobalUpdate && this.selectedInstance) {
        const GLOBAL_UPDATE = 'globalUpdate'
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, { action:GLOBAL_UPDATE, instance: this.selectedInstance})
      }
    },
    getImagesFiltered() {
      this.loaded = false;
      return this.fields.filter(field => IMG_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible && !field?.name.toLowerCase().includes('background'));
    },
    toggleProductDropdown(item, view = 'replace') {
      if (!item) {
        const activeObjects = this.selectedInstance?.canvas?.getActiveObjects()?.filter(field => field?.id?.startsWith(IMAGE_PREFIX_ID) && field.visible);
        const productImages = this.getProductImages;
        const lastProduct = productImages[productImages.length - 1];

        const lastActiveObject = this.selectedInstance?.canvas
          ?.getObjects()
          .find(obj => obj?.id === lastProduct?.id);

        const activeObject = activeObjects[0] ?? lastActiveObject;
        //open create product images if there is no active object(means all is deleted)
        if (!activeObject) {
          this.selectedInstance.canvas.fire('add:product-images', {
            instanceId: this.selectedInstance.id,
            isNeedAddProductDimensionsField: false,
          })
          return;
        } else {
          this.setActiveObject(activeObject);
        }
      } else {
        this.setActiveObject(item);
      }
      const dropdown = this.$refs.productImageDropdownFields;
      if (!dropdown) return
      setTimeout(() => {
        if (view === 'replace') {
          dropdown.openActiveReplaceMode(!dropdown.isShowDropdown);
        } else if(view === 'replace_all') {
            dropdown.openReplaceAllMode(!dropdown.isShowDropdown, true);
        } else {
          dropdown.onFocusProductIds(!dropdown.isShowDropdown);
        }
      }, 350);
    },
    updateField (field, value, selectedInstance) {
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value,
        action: 'inputText',
        field,
        selectedInstance
      })
    },
    handleClosePopup() {
      this.isShowDropdown = false;
      this.isContracted = false;
    },
    onBlurText() {
      window.isFieldsObject = false;
    },
    onImageLoad() {
      this.imagesLoadedCount++
      if (this.imagesLoadedCount >= this.getImages.length) {
        this.loaded = true
      }
    },
    setActiveObject (field) {
      this.fileNameIds = field.fileName;
      let target = null;
      if (field.type === FIELD_TYPE_BACKGROND_THEME_IMAGE) {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['id', field.id])
      } else if (field?.id?.startsWith && field.id.startsWith(IMAGE_PREFIX_ID)) {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['id', field.id])
      } else {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['element_id', field.element_id])
      }

      //check if field is part of a group
      if (!target) {
        const groupObject = this.selectedInstance.canvas.getObjects().find(obj => {
          if (obj.id.startsWith(KEY_GROUP)) {
            const children = obj.getObjects
              ? obj.getObjects()
              : (obj.objects || obj._objects || []);
            return children.some(child => 
              (child.id === field.id) || (child.element_id === field.element_id)
            );
          }
          return false;
        });
        if (groupObject) {
          const children = groupObject.getObjects
            ? groupObject.getObjects()
            : (groupObject.objects || groupObject._objects || []);
          target = children.find(child => 
            (child.id === field.id) || (child.element_id === field.element_id)
          );
        }
      }

      window.isFieldsObject = true;
      editorControlEventBroker.fire(EVENTS.SET_ACTIVE_OBJECT, { target })
    },
    shouldShowThisField(field){
      if (!field?.options) {
        return false;
      }
      const options = $.parseJSON(field.options);
      return (options.Option1 !== null && options.Option1 !== "");
    },
    redrawBackground(avoidSettingsUpdate = true) {
      this.editor.redraw()

      const width = this.target.width;
      const height = this.target.height;

      if (this.target?.set) {
        this.target.set({
          width,
          height,
          scaleX: 1,
          scaleY: 1,
          left: 0,
          top: 0,
          originX: 'left',
          originY: 'top'
        })
      }

      const patternCanvas = document.createElement('canvas')
      patternCanvas.width = width
      patternCanvas.height = height
      patternCanvas.getContext('2d')?.drawImage(this.editor.canvas, 0, 0, width, height)

      this.target.set('fill', new fabric.Pattern({
        source: patternCanvas,
        repeat: 'no-repeat'
      }))

      if (avoidSettingsUpdate) {
        const settings = deepCopy(this.editor.getSettings());
        settings.width = width;
        settings.height = height;
        this.target.updateSettings(settings);
        this.target.canvas.requestRenderAll();
      }
    },
    redraw(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value: '',
        action: 'redrawText',
      })
    },
    moveInstanceSelection(step) {
      const newSelectedInstance = this.currentPagePreviews[this.selectedInstanceIndex + step]
      newSelectedInstance.canvas.wrapperEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setSelectedInstance(newSelectedInstance.id)
    },
    async translate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.TRANSLATE, image);
    },
    handleProductDelete(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'deleteConfirmation', value: null
      })
    },
    handleImageDelete(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'deleteConfirmationImage', value: null
      })
    },

    // Convert Image to Text methods
    // Helper to find object by ID recursively (including inside groups)
    findCanvasObjectById(objects, targetId) {
      for (const obj of objects) {
        if (obj.id === targetId) return obj;
        // Search inside groups
        if (obj.type === 'group' && obj.getObjects) {
          const found = this.findCanvasObjectById(obj.getObjects(), targetId);
          if (found) return found;
        }
      }
      return null;
    },

    initTextImageClassifications() {
      // First check if we have saved text image classifications
      const savedTextClassifications = window.textImageClassifications;

      if (savedTextClassifications && Object.keys(savedTextClassifications).length > 0) {
        // Use saved text image classifications
        for (const [id, type] of Object.entries(savedTextClassifications)) {
          if (type === 'text') {
            this.textOnlyImageIds.add(id);
          }
        }

        if (this.textOnlyImageIds.size > 0) {
          this.textImagesClassified = true;
        }
        return;
      }

      // Fallback: Check Edit Mode classification for 'Text' type layers
      const savedClassifications = window.layerClassifications;

      if (!savedClassifications || Object.keys(savedClassifications).length === 0) {
        return;
      }

      const { previews } = getShowPageScope();
      const preview = previews.find(p => p.id === this.selectedInstance?.id);
      if (!preview) return;

      // Find images with fieldType 'Text' or 'Multi Line Text' from existing classification
      const images = this.getImages;
      for (const image of images) {
        const obj = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
        if (!obj) continue;

        // Check if this object was classified as text by Edit Mode
        const fieldType = obj.fieldType || obj.templateField?.type;
        if (fieldType === 'Text' || fieldType === 'Multi Line Text') {
          this.textOnlyImageIds.add(image.id);
        }
      }

      // Mark as classified if we found any text images from Edit Mode
      if (this.textOnlyImageIds.size > 0) {
        this.textImagesClassified = true;
      }
    },

    isTextOnlyImage(image) {
      return this.textOnlyImageIds.has(image.id);
    },

    async handleDetectTextImages() {
      this.isClassifyingTextImages = true;
      try {
        const { previews } = getShowPageScope();
        const preview = previews.find(p => p.id === this.selectedInstance?.id);
        if (!preview) return;

        // Use getImages (same images shown in UI) to collect layers
        const images = this.getImages;
        const layers = [];

        for (const image of images) {
          // Find the canvas object for this image (search recursively in groups)
          const obj = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
          if (!obj) continue;

          // Get image data URL
          const imageUrl = obj.getSrc?.() || obj._element?.src;
          if (!imageUrl) continue;

          try {
            // Convert to base64 if it's a URL
            let imageData = imageUrl;
            if (!imageUrl.startsWith('data:')) {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              imageData = await this.blobToBase64(blob);
            }

            layers.push({
              id: obj.id,
              image_data: imageData,
            });
          } catch (e) {
            console.warn('Could not get image data for', obj.id, e);
          }
        }

        if (layers.length === 0) {
          this.$bvToast.toast('No images found to classify', { variant: 'warning' });
          return;
        }

        // Call backend to classify
        const layoutId = window.layoutId;
        const { data } = await axios.post(`/banner-builder/layouts/${layoutId}/classify-text-images`, { layers });

        if (data.success && data.results) {
          // Update textOnlyImageIds set
          this.textOnlyImageIds = new Set();
          for (const [id, type] of Object.entries(data.results)) {
            if (type === 'text') {
              this.textOnlyImageIds.add(id);
            }
          }

          const textCount = this.textOnlyImageIds.size;
          if (textCount > 0) {
            this.$bvToast.toast(`Found ${textCount} text-only image${textCount > 1 ? 's' : ''}`, { variant: 'success' });
          } else {
            this.$bvToast.toast('No text-only images detected', { variant: 'info' });
          }
        }

        this.textImagesClassified = true;
      } catch (error) {
        console.error('Failed to detect text images:', error);
        this.$bvToast.toast('Failed to detect text images', { variant: 'danger' });
      } finally {
        this.isClassifyingTextImages = false;
      }
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    async handleConvertToText(image) {
      const { previews } = getShowPageScope();
      const preview = previews.find(p => p.id === this.selectedInstance?.id);
      if (!preview) return;

      const canvasObject = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
      if (!canvasObject) return;

      try {
        this.convertingImageId = image.id;

        // 1. Get image URL
        const imageUrl = canvasObject.getSrc?.() || canvasObject._element?.src;
        if (!imageUrl) {
          this.$bvToast.toast('Could not get image source', { variant: 'warning' });
          return;
        }

        // 2. Extract text via OCR
        const extractedText = await this.extractTextFromImage(imageUrl);
        if (!extractedText?.trim()) {
          this.$bvToast.toast('No text detected in image', { variant: 'warning' });
          return;
        }

        // 3. Get position/size from original
        const { left, top, width, height, scaleX = 1, scaleY = 1 } = canvasObject;

        // 4. Extract dominant color
        const textColor = this.extractDominantColor(canvasObject) || '#000000';

        // 5. Create text field at same position
        const textObject = new fabric.IText(extractedText, {
          left,
          top,
          width: width * scaleX,
          fontSize: Math.max(12, Math.min((height * scaleY) * 0.6, 48)),
          fill: textColor,
          fontFamily: 'Arial',
          element_id: `converted_text_${Date.now()}`,
          fieldType: 'Text',
        });

        preview.canvas.add(textObject);
        preview.canvas.setActiveObject(textObject);

        // 6. Hide original image
        canvasObject.visible = false;
        preview.canvas.requestRenderAll();

        // Remove from text-only set since it's converted
        this.textOnlyImageIds.delete(image.id);

        // Refresh fields list
        this.getFields();

        this.$bvToast.toast('Converted to editable text', { variant: 'success' });
      } catch (error) {
        console.error('Convert to text failed:', error);
        this.$bvToast.toast('Failed to convert image to text', { variant: 'danger' });
      } finally {
        this.convertingImageId = null;
      }
    },

    async extractTextFromImage(imageUrl) {
      try {
        // Convert URL to blob/file for OCR
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'image.png', { type: blob.type });

        // Call OCR endpoint
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', 'ARS'); // Amazon Rekognition

        const { data } = await axios.post('/ocr/recognize-image-text', formData);

        // Join text lines
        return Array.isArray(data) ? data.join('\n') : data;
      } catch (error) {
        console.error('OCR failed:', error);
        return null;
      }
    },

    extractDominantColor(canvasObject) {
      try {
        // Try to get dominant color from image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = canvasObject._element;

        if (!img) return null;

        canvas.width = 1;
        canvas.height = 1;
        ctx.drawImage(img, 0, 0, 1, 1);

        const pixel = ctx.getImageData(0, 0, 1, 1).data;
        return `rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`;
      } catch (e) {
        return null;
      }
    },

    async copyAndTranslate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.COPY_AND_TRANSLATE, image);
    },
    async copyFieldsAndTranslate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.COPY_FIELDS_AND_TRANSLATE, image);
    },
    async captureCanvas() {
      if (!this.selectedInstance || !this.selectedInstance.canvas) {
        return null;
      }
      return this.selectedInstance.canvas.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5,
      });
    },
    getMaxCharsValue(field) {
      let maxCharsFromBaseField = 10000;
      try {
        const options = JSON.parse(field.options)
        maxCharsFromBaseField = options['Max Chars']
      } catch (e) {
        //do nothing
      }
      return Number(this.selectedInstance.data[`${field.element_id}_max_chars`] || maxCharsFromBaseField)
    },
    handleRTLLanguages(lang) {
      const rtlLanguages = ['Arabic', 'Hebrew'];
      const isRTL = Array.isArray(lang)
        ? lang.some(l => rtlLanguages.includes(l))
        : rtlLanguages.includes(lang);

      if (isRTL) {
        const canvas = this.selectedInstance?.canvas;
        if (!canvas) return;

        const selectedObjects = canvas.getActiveObjects();
        
        selectedObjects.forEach(obj => {
          if (obj.type === 'textbox' || obj.type === 'i-text' || obj.isEditingText) {
            obj.set({ textAlign: 'right' });
            if (obj.setCoords) obj.setCoords();
          }
        });

        canvas.requestRenderAll();
      }
    },
    async emitTranslateEvent(handler, image = null) {
      if (!Array.isArray(this.translateTargetLanguages) || this.translateTargetLanguages.length === 0) {
        return;
      }

      const { saveLayout } = getShowPageScope();
      await saveLayout(true, false, 'Translating...');

      // Wait for saveLayout to finish (doesn't fully finish after await)
      setTimeout(() => {
        const lang =
          this.translateTargetLanguages.length === 1
            ? this.translateTargetLanguages[0]
            : [...this.translateTargetLanguages];

        editorControlEventBroker.fire(EVENTS.TRANSLATE, {
          textFields: this.textFields,
          multilineTextFields: this.multilineTextFields,
          additionalTextFields: this.additionalTextFields,
          additionalMultilineTextFields: this.additionalMultilineTextFields,
          instance: this.selectedInstance,
          lang,
          handler,
          image
        });

        setTimeout(() => {
          this.handleRTLLanguages(lang);
        }, 2000);
      }, 2500);
    },
    toggleLoading({ loadingType }) {
      this[loadingType] = !this[loadingType];
    },
    getValidatedValue(value) {
      return typeof value === 'boolean' ? '' : (value || '');
    },
    async confirmApply() {
      
      const selectedTemplates = localStorage.getItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT
      );

      // set seleted templates for BG_COLOR
      localStorage.setItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
        selectedTemplates
      );

      localStorage.setItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE,
        selectedTemplates
      );

      const { previews } = getShowPageScope();
      const preview = previews.find(preview => preview.id === this.selectedInstance.id);
      if (!preview) {
        return;
      }
      const objects = preview.canvas.getObjects().filter(object => object.visible === true && object.element_id?.includes(TEXT_PREFIX_ID));
      if (Array.isArray(objects) && objects.length > 0) {
        const selection = new fabric.ActiveSelection(objects, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();

        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
          action: 'text',
          value: true,
          keys: [],
        });
      }

      //if no background color is set skip
      if (this.editor?.colors[0] !== this.defaultColor) { //#fff is default color on mounted
        editorControlEventBroker.fire(EVENTS.BG_COLOR_CHANGED, [{
          text: 'Update Color',
          key: 'update_color',
          value: false,
          instance_id: this.target.canvas.instance_id,
          backgroundElementId: this.target.id,
          objectSearch: 'fieldType'
        }]);
      }

      //if product images are empty skip apply
      if (this.getProductImages.length > 0) {
          await this.selectedInstance.canvas.fire('product-images-selector:apply-to', this.selectedInstance.id);
      }
 
      // setSelectedInstance(testInstance.id)
      //if background image is empty skip
      if (this.selectedBackground) {
        const selectObject = preview.canvas.getObjects().filter(object => object.id === this.selectedBackground.id);
        const selection = new fabric.ActiveSelection(selectObject, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();
        await editorControlEventBroker.fire(EVENTS.CHANGE_IMAGE_PROPERTIES, [{
          text: 'Replace images',
          key: 'replace',
          value: false
        }]);
      }

      if (this.getImages.length > 0) {
        //get only the images
        const selectObject = preview.canvas.getObjects().filter(object => this.getImages.some(image => image.id === object.id));
        const selection = new fabric.ActiveSelection(selectObject, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();
        await editorControlEventBroker.fire(EVENTS.CHANGE_IMAGE_PROPERTIES, [{
          text: 'Replace images',
          key: 'replace',
          value: false
        }]);
      }
    },
    getAutoPositionValue(field) {
      if (Object.hasOwn(this.selectedInstance.data, `${field.element_id}_auto_position`)) {
        const value = this.selectedInstance.data[`${field.element_id}_auto_position`];
        return value === 'true' || value === true;
      }

      return false
    },
    onChangeAutoPositionValue(field, value) {
      this.setActiveObject(field);

      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value: value,
        action: 'autoPosition',
      });
    },
    onChange(action, value, file = null) {
      if (action === 'selectBackground') {
        if (!this.selectedBackground) {
          toastr.error("Please select a Background Image first.");
          return;
        }
        window.setAsActiveReplacedImage = false;
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
          action, value, background_id: this.selectedBackground.id, file
        })

        return;
      }
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    handleFile(file) {
      this.onChange('selectBackground', 'Replace', file);
    },
    onDragEnter(e) {
      this.isDragOver = true;
    },

    onDragOver(e) {
      this.isDragOver = true;
    },

    onDragLeave(e) {
      // Only set to false if leaving the button area completely
      if (!this.$refs.uploadButton.contains(e.relatedTarget)) {
        this.isDragOver = false;
      }
    },

    onDrop(e) {
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        // Validate file type
        if (file.type.startsWith('image/')) {
          this.handleFile(file);
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },

    
    addAdditionalField(type, file = null, selectImageOpenSource = null) {
      document.querySelector('.back-to-folder-link').click();

      const existingElementIds = new Set();
      const existingIds = new Set();
      
      const currentObjects = this.selectedInstance.canvas.getObjects();
      currentObjects.forEach((obj, index) => {
        if (obj.element_id) existingElementIds.add(obj.element_id);
        if (obj.id) existingIds.add(obj.id);
      });
      
      this.selectedInstance.canvas.once('object:added', (e) => {
        const file = e.target?._file || null;
        setTimeout(() => {
          const updatedObjects = this.selectedInstance.canvas.getObjects();
          
          let newObject = null;
          
          updatedObjects.forEach((obj, index) => {
            if (obj.element_id && !existingElementIds.has(obj.element_id)) {
              newObject = obj;
              }
            else if (obj.id && !existingIds.has(obj.id)) {
              if (!newObject) newObject = obj;
              }
          });
          
          if (!newObject && updatedObjects.length > currentObjects.length) {
            newObject = updatedObjects[updatedObjects.length - 1];
          }
          
          if (newObject) {
            this.selectedInstance.canvas.setActiveObject(newObject);
            this.selectedInstance.canvas.renderAll();
            
            editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
              action: 'selectBackground',
              file,
              selectImageOpenSource
            });
          }
        }, 50);
      });
      
      this.selectedInstance.canvas.fire('add:additional-field', {
        instancesIds: [this.selectedInstance.id],
        type,
        options: {
          file,
          deleteWhenUserClosesImageSelectionModal: true,
        }
      })
    },
    onDropReplaceLayoutImage(e, field) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.replaceImage(field, 'selectBackground', 'Replace', file)
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    onDropAddImage(e) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.addAdditionalField('Background Theme Image', file)
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    replaceImage(field, action, value, file = null, selectImageOpenSource = null)
    {
      this.setActiveObject(field);

      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action,
        value,
        file,
        selectImageOpenSource
      })
    },
    replaceAllImage(action, value)
    {
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects()?.filter(field => IMG_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible && !field?.templateField?.name?.toLowerCase().includes('background'));
      const field = this.getImages[this.getImages.length - 1]; //if there are no active objects in canvas select the last image
      this.setActiveObject(activeObjects[0] ?? field);

      window.fieldsReplaceAllImage = true;

      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action,
        value,
        selectImageOpenSource: SELECT_IMAGE_OPEN_SOURCES.FIELDS_REPLACE_IMAGES
      })
    },
    onInputColor(inputColor, index) {
      if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(inputColor)) {
        this.$set(this.editor.colors, index, inputColor);
        this.redrawBackground();
      } else {
        toastr.error("Invalid color format. Please use hex color codes.");
      }
    },
    onDragEnterProductImages(e) {
      this.isDragOverProductImages = true;
    },

    onDragOverProductImages(e) {
      this.isDragOverProductImages = true;
    },

    onDragLeaveProductImages(e) {
      // Only set to false if leaving the dropzone completely
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.isDragOverProductImages = false;
      }
    },

    async onDropReplaceProductImage(e, image) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.setActiveObject(image);
          const dragDropService = new TemplateDragDropService(window.previews, window.template_data);
          await dragDropService.handleProductImageDrop({
            file,
            targetPreview: this.selectedInstance,
            replaceProductImage: image
          });
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    
    async onDropProductImage(e) {
      this.isDragOverProductImages = false;

      const files = e.dataTransfer.files;

      // Validation: Max 1 file
      if (files.length === 0) {
        toastr.error('No file selected');
        return;
      }

      if (files.length > 1) {
        toastr.error('Please upload only 1 file at a time');
        return;
      }

      const file = files[0];

      if (!validateProductImage(file)) {
        return;
      }

      await uploadProductImageAndDraw({
        file,
        canvas: this.selectedInstance.canvas,
        selectedLayoutId: this.selectedInstance.id,
        existingImages: this.getProductImages
      });
    },
    pasteElement() {
      copyPasteEventBroker.fire(COPY_PASTE_EVENTS.PASTE, {
        canvas: this.target.canvas
      });
    },
    handleSectionsState() {
      if (this.typeEditorControl === 'TEXT' && this.isOpen.textEditorFieldsSection === false) {
        this.$emit('toggleSection', 'textEditorFieldsSection');
      } else if (this.typeEditorControl === 'IMAGE') {
        if (this.activeTarget?.id?.startsWith(IMAGE_PREFIX_ID) && this.isOpen.productImagesFieldsSection === false) {
          this.$emit('toggleSection', 'productImagesFieldsSection');
          return;
        } else if (IMG_PREFIXES.some(prefix => this.activeTarget?.id.startsWith(prefix)) && this.isOpen.imagesFieldSection === false) {
          this.$emit('toggleSection', 'imagesFieldSection');
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.background-dropdown-label,
.background-dropdown-option {
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.background-dropdown-label__image,
.background-dropdown-option__image {
  position: relative;
  width: 100%;
  flex-shrink: 0;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.background-dropdown-label__image {
  max-width: 50px;
  padding-top: 50px;
}

.background-dropdown-option__image {
  max-width: 30px;
  padding-top: 30px;
}

.background-dropdown-option__text {
  margin: 0;
}

.translation-form-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    ::v-deep {
        ul.dropdown-menu {
            width: 100%;
        }
    }
}

  .button-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  button {
    white-space: nowrap;
  }

  .maintain-button-width {
    min-width: max-content;
  }
  .colors-item {
    width: 100%;
    padding-bottom: 10px;
  }
  .colors-item input {
    // background-color: #f8f9fa !important;
    border-radius: 0.35rem !important;
    border-color: #e9ecef !important;
  }
  .multiselect {
    min-width: 80px;
  }
  .background-dropdown {
    .background-dropdown {
        margin-top: 10px;
      }
      .background-dropdown-label {
        width: 60px;
        height: 22px;
      }

      ::v-deep(.multiselect__select) {
        left: 50px !important;
        right: auto;
      }

      ::v-deep(.multiselect__single) {
        padding-left: 0 !important;
      }
  }
  
::v-deep(.background-dropdown .multiselect__content-wrapper) {
  width: 245px;
  box-sizing: border-box;
  left: -5px;
}

.multiselect__single .background-dropdown-label__image{
  max-width: 28px !important;
  padding-top: 35px !important;
}

.preview-img:hover .delete-overlay {
    display: block;
}
.cloud-overlay-icon {
  position: absolute;
  z-index: 10;
  top: 0;
  left: -15px;
  cursor: pointer;
}
.background-dropdown-label__image {
  margin-left: 2px;
}
.label-option {
  position: relative;
  margin: 0 5px;
}
.image-option {
  width: 30px;
  height: auto;
}
.label-option .close-btn {
  opacity: 0; 
  position: absolute;
  top: 1px;
  right: 2px;
  background-color: red;
  vertical-align: -.15em;
  color: white;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  transition: opacity 0.3s ease-in-out;
}

.replace-image-btn i {
  color: #000;
}

.label-option .replace-image-btn {
  position: absolute;
  bottom: 0;
  left: 2px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border: 0;
  background: transparent;
  padding: 0;
  color: #209c86;
}

.label-option:hover .close-btn,
.label-option:hover .replace-image-btn {
  opacity: 1;
}

.dropdown-product-images {
  & input:hover { 
    cursor: pointer;
  }
}

.product-ids-input[readonly] {
    background: #fff;
}

.file-browser-wrapper {
    max-height: 770px;
    overflow-y: auto;
    ::v-deep {
        .c-main-view {
            border: none;
        }
    }
}

.buttons-wrapper {
  display: flex;
  gap: 10px;
  margin: 15px 10px 0 0;
  justify-content: end;
}
::v-deep {
  .products-container {
    width: calc(65vw - 9px);
    height: fit-content;
    position: fixed;
    background: rgb(255, 255, 255);
    z-index: 101;
    top: 87px;
    right: 64px;
    border: 1px solid;
    box-shadow: 2px 2px #888888;
    overflow-y: auto;
    padding: 20px;
  }
  
  .contract-btn-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .contract-btn, .close-btn {
    cursor: pointer;
  }

  .products-container--contracted {
    width: 400px;
    padding: 5px 20px;
    
    .file-browser-wrapper {
      display: none;
    }
    
    .buttons-wrapper {
      display: none;
    }
  }
}

.replace-options-dropdown {
  .list-group {
    min-width: 300px;
    .list-group-item {
      &:first-child {
        border-top: 0;
      }
      &:last-child {
        border-bottom: 0;
      }
    }
    
    
  }
  .dropdown-menu.show {
    padding-top:0 !important;
    padding-bottom: 0 !important;
    border:0 !important;
  }
}

.add-icon {
  cursor: pointer;
}

.color-fill-item {
  height: 38px !important;
  padding: 7px 7px !important;
  width: 15% !important;
}

.color-fill-item:hover {
  cursor: pointer;
}

.color-fill-item:focus {
  box-shadow: none !important;
  outline: none !important;
  border-color: #ced4da !important;
}

::v-deep .multiselect__tags {
  min-height: 40px !important;
  border: 0 !important;
}

@media screen and (max-height: 730px) {
  .file-browser-wrapper {
    max-height: 400px;
  }
  
  .products-container {
    height: 500px;
  }

  .products-container--contracted {
    height: auto;
    width: 400px;
    padding: 5px 20px;
  }
  
}
.fields-module strong {
  font-size: 0.875rem !important;
  letter-spacing: 0.3px;
}
.green-fonts {
  color: #209c86 !important;
  margin-right: 5px;
}
.upload-button {
  border-radius: 0.35rem !important;
  border: 1px solid #e9ecef;
  padding-right: 0;
  padding-left: 10px;
  background-color: #209c86 !important;
  color: white;
  box-shadow: none !important;
}

.upload-button:hover {
  background-color: #209c86 !important;
}

.upload-button i {
  filter: drop-shadow(0 0 0 currentColor);
  margin-bottom: 12px !important;
}
.general-card-template {
  display: inline-block;
  margin: 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  background: #f4f4f4;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.image-card-template {
  width: 90px;
  height: 90px;
}
.image-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.additional-card-template {
  margin: 8px;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  width: 90px;
  height: 90px;
}
.product-card-template {
  width: 65px;
  height: 65px;
}
.product-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.image-loader {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
img.is-loading {
  filter: blur(6px);
  opacity: 0.5;
}
.scroll-images-section{
  width: 100%;
  max-height: 222px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  scrollbar-width: thin;
}
.colors-item  .no-right-border {
  border-right: none !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.colors-item  .no-left-border {
  border-left: none !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.image-multiselect{
  border: 1px solid #e9ecef !important;
  padding: 4px;
  background: white;
  border-radius: 6px;
}

.custom-color-picker {
  -webkit-appearance: none; /* safari support */
  -moz-appearance: none;    /* firefox support */
  appearance: none; 
  width: 42px !important;      
  height: 38px !important;   
  border-radius: 8px; 
  overflow: hidden;
  padding: 0;
  cursor: pointer;
}

.custom-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.custom-color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px; 
}

.custom-color-picker::-moz-color-swatch {
  border: none;
  border-radius: 4px;
}

.color-fill-item:focus {
  border-color: none !important;
}

.custom-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  color: #777;
}

.ms-bootstrap-size {
  font-size: var(--bs-body-font-size, 1rem);
  line-height: var(--bs-body-line-height, 1.5);
}

.ms-bootstrap-size {
  ::v-deep(.multiselect__placeholder),
  ::v-deep(.multiselect__input),
  ::v-deep(.multiselect__single),
  ::v-deep(.multiselect__tags) {
    font-size: inherit;
    line-height: inherit;
  }

  ::v-deep(.multiselect__input::placeholder) {
    font-size: inherit;
    line-height: inherit;
  }
}
.apply-to-section{
  bottom: 44px;
  z-index: 1;
}
.selected {
  border: 3px solid #059669;
}

/* Convert Image to Text feature styles */
.detect-text-btn {
  font-size: 11px;
  padding: 2px 8px;
  white-space: nowrap;
}

.convert-text-btn {
  position: absolute;
  bottom: 4px;
  left: 4px;
  padding: 2px 5px;
  background: #E3F2FD;
  border: 1px solid #BBDEFB;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  &:hover {
    background: #BBDEFB;
  }

  i {
    color: #1565C0;
    font-size: 12px;
  }
}

.image-card-template:hover .convert-text-btn {
  opacity: 1;
}
</style>
